// src/components/accounts/AccountsEditPanel.js
// ✅ FULL DROP-IN REPLACEMENT
//
// ✅ Fixes BOTH issues you reported:
//
// 1) AWFUL LOAD TIME FIX
//    - Modal opens immediately (no full-screen "Loading..." gate)
//    - Company static data (company/groups/territories/fieldDefs/profile) is:
//        ✅ loaded in parallel
//        ✅ cached per companyId (no refetch on every account click)
//    - Staff users (for Assigned dropdown) is:
//        ✅ ONLY loaded for Admin/Owner
//        ✅ loaded AFTER modal renders (non-blocking)
//        ✅ cached per companyId
//
// 2) "Manage Portal Access" NAV BUG FIX
//    - Uses robust RELATIVE navigation first (works whether you use basename="/app" or not)
//    - Includes a safe fallback if your router setup is unusual
//    - Stops click bubbling so it won't accidentally trigger overlay/row handlers
//
// ✅ PORTAL ACCESS (per request):
// - NO sending invites here
// - ONLY shows active portal customer users + pending portal invites
// - "Manage Portal Access" routes to Team → Invites with query params:
//     team?tab=invites&accountId=XYZ
//
// NOTE: Team screen doesn't HAVE to parse query params yet; navigation will still work.
//       If your Team route exists, you will land there. If not, your app may redirect.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import {
  collection,
  doc,
  serverTimestamp,
  getDocs,
  getDoc,
  query,
  where,
  updateDoc,
  deleteDoc,
  limit,
} from "firebase/firestore";
import { db } from "../../firebase";
import { getTerritoryLabel, resolveAccountTerritory } from "../../utils/accountTerritory";
import { moveDocToRecycleBin } from "../../utils/recycleBinService";
import {
  Trash2,
  X,
  Lock,
  Users,
  Loader2,
  CheckCircle2,
  Mail,
  Clock,
  ExternalLink,
} from "lucide-react";

const STAGES = ["Prospect", "Sampling", "Customer", "Churn Risk", "Inactive"];
const FLAGS = ["None", "Sample Abuse", "Repeat Samples", "Converted Orders", "Do Not Serve"];

const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

// Shared styles
const inputBase =
  "w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
const inputDisabled = "bg-slate-50 text-slate-500 cursor-not-allowed";

// ---------------- Helpers ----------------
const safeArray = (v) => (Array.isArray(v) ? v : []);
const normalizeEmail = (v) => (v || "").toString().trim().toLowerCase();
const normalizeStr = (v) => (v || "").toString().trim().toLowerCase();
const uniqStrings = (arr) => Array.from(new Set(safeArray(arr).filter(Boolean).map(String)));

const isActiveStatus = (status) => {
  const s = normalizeStr(status);
  return !s || s === "active";
};

const isPendingInviteStatus = (status) => normalizeStr(status) === "pending";

const formatDate = (ts) => {
  try {
    if (!ts) return "—";
    const d =
      typeof ts?.toDate === "function"
        ? ts.toDate()
        : ts?.seconds
        ? new Date(ts.seconds * 1000)
        : new Date(ts);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  } catch {
    return "—";
  }
};

const timestampToInputDate = (ts) => {
  if (!ts) return "";
  try {
    let d;
    if (typeof ts?.toDate === "function") d = ts.toDate();
    else if (ts?.seconds) d = new Date(ts.seconds * 1000);
    else d = new Date(ts);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

const parseInputDate = (value) => {
  if (!value) return null;
  const d = new Date(value + "T00:00:00");
  return Number.isNaN(d.getTime()) ? null : d;
};

const getPrimaryContactName = (acc = {}) =>
  acc?.primaryContact?.name || acc?.contactName || acc?.contact || "";

const getPrimaryContactEmail = (acc = {}) =>
  acc?.primaryContact?.email || acc?.contactEmail || acc?.email || "";

const getAssignedInternalUserId = (acc = {}) => acc.assignedToId || acc.assignedUserId || null;

const getAccountGroupIds = (acc = {}) => {
  const ids = acc.groupIds || acc.groups || [];
  return uniqStrings(
    safeArray(ids)
      .map((x) => (typeof x === "string" ? x : x?.id || null))
      .filter(Boolean)
  );
};

const isTerritoryOverridden = (acc = {}) => acc.territoryOverride === true;

// ---------------- Custom field de-dupe ----------------
const RESERVED_CUSTOM_FIELD_KEYS = new Set([
  "active",
  "name",
  "phone",
  "locationphone",
  "primarycontactname",
  "primarycontactemail",
  "contact",
  "contactname",
  "email",
  "contactemail",
  "stage",
  "flaggedas",
  "visited",
  "lastvisitedat",
  "lastvisiteddate",
  "hassampleorder",
  "notes",
  "addressstreet",
  "addressstreet2",
  "addresscity",
  "addressstate",
  "addresszip",
  "city",
  "state",
  "zip",
  "assignedtoid",
  "assigneduserid",
  "assignedtoname",
  "territoryid",
  "territoryname",
  "territoryoverride",
  "groupids",
]);

const isReservedCustomFieldDef = (def) => {
  const key = normalizeStr(def?.fieldKey || def?.id || "");
  const label = normalizeStr(def?.label || "");
  if (key && RESERVED_CUSTOM_FIELD_KEYS.has(key)) return true;
  if (label === "active") return true;
  return false;
};

const stripReservedCustomKeys = (obj) => {
  const out = { ...(obj || {}) };
  Object.keys(out).forEach((k) => {
    const nk = normalizeStr(k);
    if (RESERVED_CUSTOM_FIELD_KEYS.has(nk)) delete out[k];
  });
  return out;
};

const AUTO_TERRITORY = "__AUTO__";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// FieldRow component
const FieldRow = ({ label, required, right, children }) => (
  <div className="flex items-center gap-2 min-w-0">
    <label className="text-xs font-medium text-slate-700 whitespace-nowrap w-28 shrink-0">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    <div className="flex-1 min-w-0">{children}</div>
    {right && <div className="shrink-0">{right}</div>}
  </div>
);

const AccountsEditPanel = ({ isOpen, onClose, companyId, account, onUpdated }) => {
  const navigate = useNavigate();
  const auth = useMemo(() => getAuth(), []);
  const accountId = account?.id || null;

  const mountedRef = useRef(true);
  const didAutoFillContactRef = useRef(false);

  // --- caching refs (PERFORMANCE) ---
  const staticLoadedForCompanyRef = useRef(null);
  const staffLoadedForCompanyRef = useRef(null);
  const loadingStaticRef = useRef(false);
  const loadingStaffRef = useRef(false);
  const lastPortalLoadedAccountRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [myProfile, setMyProfile] = useState(null);

  const myRole = useMemo(() => normalizeStr(myProfile?.role || ""), [myProfile]);
  const isAdmin = useMemo(() => myRole === "admin" || myRole === "owner", [myRole]);
  const canManagePortalAccess = useMemo(() => myRole !== "customer" && !!myRole, [myRole]);

  const [companyData, setCompanyData] = useState(null);

  // Static data (cached)
  const [groups, setGroups] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [fieldDefs, setFieldDefs] = useState([]);

  // Staff users (only needed for admin coverage edit)
  const [teamUsers, setTeamUsers] = useState([]);

  // Non-blocking loading flags
  const [staticLoading, setStaticLoading] = useState(false);
  const [staffLoading, setStaffLoading] = useState(false);

  const [groupSearch, setGroupSearch] = useState("");

  const [portalLinks, setPortalLinks] = useState({
    activeUsers: [],
    pendingInvites: [],
  });
  const [portalLoading, setPortalLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    locationPhone: "",
    assignedToId: "",
    territoryOverride: false,
    territoryId: "",
    groupIds: [],
    primaryContactName: "",
    primaryContactEmail: "",
    stage: "Prospect",
    flaggedAs: "None",
    visited: false,
    lastVisitedDate: "",
    hasSampleOrder: false,
    active: true,
    notes: "",
    addressStreet: "",
    addressStreet2: "",
    addressCity: "",
    addressState: "",
    addressZip: "",
  });

  const [customValues, setCustomValues] = useState({});

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const teamUsersById = useMemo(() => {
    const m = new Map();
    teamUsers.forEach((u) => m.set(u.id, u));
    return m;
  }, [teamUsers]);

  const territoriesById = useMemo(() => {
    const m = new Map();
    territories.forEach((t) => m.set(t.id, t));
    return m;
  }, [territories]);

  const filteredGroups = useMemo(() => {
    const q = (groupSearch || "").toLowerCase().trim();
    if (!q) return groups;
    return groups.filter((g) => (g?.name || "").toLowerCase().includes(q));
  }, [groups, groupSearch]);

  const autoTerritory = useMemo(() => {
    const assignedUserCandidate = form.assignedToId
      ? teamUsersById.get(form.assignedToId)
      : null;
    return resolveAccountTerritory({
      territories,
      address: {
        addressCity: form.addressCity,
        addressState: form.addressState,
        addressZip: form.addressZip,
      },
      assignedUser: assignedUserCandidate,
    });
  }, [territories, form.addressCity, form.addressState, form.addressZip, form.assignedToId, teamUsersById]);

  const autoTerritoryLabel = autoTerritory?.name || null;
  const autoTerritoryDetail = useMemo(() => {
    if (!autoTerritory?.matchedBy) return "No territory match yet";
    if (autoTerritory.matchedBy === "assigned_default") {
      return "Using the selected rep's assigned territory";
    }
    if (autoTerritory.matchedBy.startsWith("assigned_")) {
      return "Matched within the selected rep's assigned territories";
    }
    if (autoTerritory.matchedBy === "zip") return "Matched by ZIP";
    if (autoTerritory.matchedBy === "city") return "Matched by city";
    if (autoTerritory.matchedBy === "state") return "Matched by state";
    return "Auto-detected territory";
  }, [autoTerritory]);

  // ---------------- PERFORMANCE: load static data ONCE per company (parallel + cached) ----------------
  const ensureStaticLoaded = async () => {
    if (!companyId) return;

    // If already loaded for this company, skip
    if (
      staticLoadedForCompanyRef.current === companyId &&
      companyData?.id === companyId &&
      Array.isArray(groups) &&
      Array.isArray(territories) &&
      Array.isArray(fieldDefs)
    ) {
      return;
    }

    if (loadingStaticRef.current) return;
    loadingStaticRef.current = true;
    setStaticLoading(true);

    try {
      const uid = auth.currentUser?.uid;

      const profilePromise = uid ? getDoc(doc(db, "users", uid)) : Promise.resolve(null);
      const companyPromise = getDoc(doc(db, "companies", companyId));
      const groupsPromise = getDocs(collection(db, "companies", companyId, "groups"));
      const terrPromise = getDocs(collection(db, "companies", companyId, "territories"));
      const defsPromise = getDocs(collection(db, "companies", companyId, "accountFieldDefinitions"));

      const [profileSnap, companySnap, groupsSnap, terrSnap, defsSnap] = await Promise.all([
        profilePromise,
        companyPromise,
        groupsPromise,
        terrPromise,
        defsPromise,
      ]);

      if (profileSnap && profileSnap.exists()) {
        setMyProfile({ id: profileSnap.id, ...profileSnap.data() });
      }

      const company = companySnap.exists()
        ? { id: companySnap.id, ...companySnap.data() }
        : null;
      setCompanyData(company);

      const groupsList = groupsSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setGroups(groupsList);

      const terrList = terrSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setTerritories(terrList);

      const defs = defsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      defs.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      setFieldDefs(defs);

      staticLoadedForCompanyRef.current = companyId;
    } catch (err) {
      console.error("[AccountsEditPanel] ensureStaticLoaded error:", err);
    } finally {
      loadingStaticRef.current = false;
      if (mountedRef.current) setStaticLoading(false);
    }
  };

  // Load staff list ONLY if admin/owner (non-blocking, cached)
  const ensureStaffLoadedIfAdmin = async () => {
    if (!companyId || !isAdmin) return;

    if (staffLoadedForCompanyRef.current === companyId && Array.isArray(teamUsers) && teamUsers.length > 0) {
      return;
    }

    if (loadingStaffRef.current) return;
    loadingStaffRef.current = true;
    setStaffLoading(true);

    try {
      const usersSnap = await getDocs(
        query(collection(db, "users"), where("companyId", "==", companyId))
      );

      const allUsers = usersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const staff = allUsers
        .filter((u) => normalizeStr(u.role) !== "customer")
        .sort((a, b) => {
          const an = (a.fullName || a.displayName || a.email || "").toLowerCase();
          const bn = (b.fullName || b.displayName || b.email || "").toLowerCase();
          return an.localeCompare(bn);
        });

      setTeamUsers(staff);
      staffLoadedForCompanyRef.current = companyId;
    } catch (err) {
      console.error("[AccountsEditPanel] ensureStaffLoadedIfAdmin error:", err);
    } finally {
      loadingStaffRef.current = false;
      if (mountedRef.current) setStaffLoading(false);
    }
  };

  // Kick off static loads when modal opens (non-blocking)
  useEffect(() => {
    if (!isOpen || !companyId) return;
    ensureStaticLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, companyId]);

  // After profile resolves, if admin -> load staff users (non-blocking)
  useEffect(() => {
    if (!isOpen || !companyId) return;
    if (!isAdmin) return;
    ensureStaffLoadedIfAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, companyId, isAdmin]);

  // Load portal status for this account (ACTIVE customer users + PENDING customer invites)
  const loadPortalStatus = async (acc, contactEmailMaybe) => {
    if (!companyId || !acc?.id) {
      setPortalLinks({ activeUsers: [], pendingInvites: [] });
      return { bestName: "", bestEmail: "" };
    }

    // Avoid reloading portal status repeatedly for same account open
    if (lastPortalLoadedAccountRef.current === acc.id) {
      return { bestName: "", bestEmail: "" };
    }
    lastPortalLoadedAccountRef.current = acc.id;

    setPortalLoading(true);
    try {
      const usersRef = collection(db, "users");
      const invitesRef = collection(db, "invites");

      // Active portal users (single-field query)
      const usersSnap = await getDocs(query(usersRef, where("accountId", "==", acc.id), limit(50)));
      const activeUsers = usersSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((u) => (u.companyId || "") === companyId)
        .filter((u) => normalizeStr(u.role) === "customer")
        .filter((u) => isActiveStatus(u.status))
        .sort((a, b) => (a.fullName || a.email || "").localeCompare(b.fullName || b.email || ""));

      // Pending portal invites (single-field query)
      const invSnap = await getDocs(query(invitesRef, where("accountId", "==", acc.id), limit(50)));
      const pendingInvites = invSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((i) => (i.companyId || "") === companyId)
        .filter((i) => normalizeStr(i.role) === "customer")
        .filter((i) => isPendingInviteStatus(i.status))
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

      setPortalLinks({ activeUsers, pendingInvites });

      // Best guess contact autofill if missing
      const seedEmail = normalizeEmail(contactEmailMaybe || getPrimaryContactEmail(acc));
      const bestActive =
        (seedEmail && activeUsers.find((u) => normalizeEmail(u.email) === seedEmail)) ||
        activeUsers[0] ||
        null;
      const bestPending =
        (seedEmail && pendingInvites.find((i) => normalizeEmail(i.email) === seedEmail)) ||
        pendingInvites[0] ||
        null;

      const bestEmail =
        normalizeEmail(bestActive?.email) || normalizeEmail(bestPending?.email) || seedEmail || "";

      const bestName =
        (bestActive?.fullName || bestActive?.name || "").trim() ||
        (bestPending?.fullName || bestPending?.name || "").trim() ||
        (getPrimaryContactName(acc) || "").trim() ||
        "";

      return { bestName, bestEmail };
    } catch (err) {
      console.error("[AccountsEditPanel] loadPortalStatus error:", err);
      setPortalLinks({ activeUsers: [], pendingInvites: [] });
      return { bestName: "", bestEmail: "" };
    } finally {
      if (mountedRef.current) setPortalLoading(false);
    }
  };

  // Populate form immediately on open/account change (FAST)
  useEffect(() => {
    if (!isOpen || !companyId || !accountId || !account) return;

    didAutoFillContactRef.current = false;
    lastPortalLoadedAccountRef.current = null; // allow portal reload for new account

    const primaryContactName = getPrimaryContactName(account);
    const primaryContactEmail = getPrimaryContactEmail(account);
    const assignedToId = getAssignedInternalUserId(account) || "";
    const overridden = isTerritoryOverridden(account);
    const storedTerritoryId = account.territoryId || "";
    const initialGroupIds = getAccountGroupIds(account);
    const addr = account.address || {};

    const legacyActive =
      typeof account?.active === "boolean"
        ? null
        : (() => {
            const cf = account?.customFields || {};
            const v = cf.active ?? cf.Active ?? cf.ACTIVE;
            return typeof v === "boolean" ? v : null;
          })();

    const initialActive =
      typeof account?.active === "boolean" ? account.active : legacyActive ?? true;

    setForm({
      name: account.name || "",
      locationPhone: account.phone || account.locationPhone || "",
      assignedToId,
      territoryOverride: !!overridden,
      territoryId: overridden ? storedTerritoryId : "",
      groupIds: initialGroupIds,
      primaryContactName,
      primaryContactEmail,
      stage: account.stage || "Prospect",
      flaggedAs: account.flaggedAs || "None",
      visited: !!account.visited,
      lastVisitedDate: timestampToInputDate(account.lastVisitedAt),
      hasSampleOrder: !!account.hasSampleOrder,
      active: initialActive !== false,
      notes: account.notes || "",
      addressStreet: account.addressStreet || addr.line1 || account.street || "",
      addressStreet2: account.addressStreet2 || addr.line2 || account.addressLine2 || "",
      addressCity: account.addressCity || addr.city || account.city || "",
      addressState: account.addressState || addr.state || account.state || "",
      addressZip: account.addressZip || addr.zip || account.zip || "",
    });

    setCustomValues(stripReservedCustomKeys(account.customFields || {}));
    setGroupSearch("");

    let cancelled = false;
    (async () => {
      const { bestName, bestEmail } = await loadPortalStatus(account, primaryContactEmail);
      if (cancelled) return;

      setForm((prev) => {
        const prevEmail = normalizeEmail(prev.primaryContactEmail);
        const prevName = (prev.primaryContactName || "").trim();
        const next = { ...prev };

        if (!didAutoFillContactRef.current) {
          if (!prevEmail && bestEmail) next.primaryContactEmail = bestEmail;
          if (!prevName && bestName) next.primaryContactName = bestName;

          if (
            next.primaryContactEmail !== prev.primaryContactEmail ||
            next.primaryContactName !== prev.primaryContactName
          ) {
            didAutoFillContactRef.current = true;
          }
        }
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, companyId, accountId]);

  if (!isOpen || !accountId) return null;

  const assignedUser = form.assignedToId ? teamUsersById.get(form.assignedToId) : null;

  const effectiveTerritoryLabel = (() => {
    if (form.territoryOverride && form.territoryId) {
      const t = territoriesById.get(form.territoryId);
      return getTerritoryLabel(t) || form.territoryId;
    }
    return autoTerritoryLabel || "— (no match)";
  })();

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleCustomChange = (fieldKey, value) =>
    setCustomValues((prev) => ({ ...prev, [fieldKey]: value }));

  const toggleGroup = (groupId) => {
    if (!groupId) return;
    setForm((prev) => {
      const set = new Set(uniqStrings(prev.groupIds));
      const gid = String(groupId);
      if (set.has(gid)) set.delete(gid);
      else set.add(gid);
      return { ...prev, groupIds: Array.from(set) };
    });
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();
    if (saving) return;
    if (!companyId || !accountId) return;

    const trimmedName = form.name.trim();
    if (!trimmedName) {
      alert("Location name is required.");
      return;
    }

    const contactEmailInput = normalizeEmail(form.primaryContactEmail) || "";
    const requiresContactEmail = form.active !== false;
    const requiresAssignedRep =
      isAdmin &&
      form.active !== false &&
      (String(form.stage || "").trim() !== "Prospect" || !!form.hasSampleOrder || !!form.visited);

    if (requiresContactEmail && !contactEmailInput) {
      alert("An active account needs a primary contact email.");
      return;
    }

    if (contactEmailInput && !EMAIL_REGEX.test(contactEmailInput)) {
      alert("Please enter a valid primary contact email.");
      return;
    }

    if (requiresAssignedRep && !(form.assignedToId || "").trim()) {
      alert("Assign a rep before saving an active account with sample or customer activity.");
      return;
    }

    setSaving(true);
    let savedOk = false;

    try {
      const ref = doc(db, "companies", companyId, "accounts", accountId);

      let lastVisitedAt = null;
      if (form.visited) {
        const d = parseInputDate(form.lastVisitedDate);
        lastVisitedAt = d || serverTimestamp();
      }

      const cleanedCustom = {};
      (Array.isArray(fieldDefs) ? fieldDefs : []).forEach((def) => {
        if (isReservedCustomFieldDef(def)) return;
        const key = def.fieldKey || def.id;
        const raw = customValues[key];
        if (raw === undefined || raw === null || raw === "") return;

        const type = (def.type || "text").toLowerCase();
        if (type === "number") {
          const n = Number(raw);
          if (!Number.isNaN(n)) cleanedCustom[key] = n;
        } else if (type === "boolean") {
          cleanedCustom[key] = !!raw;
        } else {
          cleanedCustom[key] = raw;
        }
      });

      const contactName = (form.primaryContactName || "").trim() || null;
      const contactEmail = normalizeEmail(form.primaryContactEmail) || null;

      const existingAssignedToId = getAssignedInternalUserId(account);
      const existingGroupIds = getAccountGroupIds(account);

      const assignedToId = isAdmin ? (form.assignedToId || "").trim() || null : existingAssignedToId;

      const assignedUserLocal = assignedToId ? teamUsersById.get(assignedToId) : null;
      const assignedToName = assignedUserLocal
        ? assignedUserLocal.fullName || assignedUserLocal.displayName || assignedUserLocal.email || null
        : null;

      const street1 = form.addressStreet.trim() || null;
      const street2 = form.addressStreet2.trim() || null;
      const city = form.addressCity.trim() || null;
      const state = form.addressState.trim() || null;
      const zip = form.addressZip.trim() || null;

      const existingCountry = account?.address?.country || null;
      const hasAnyAddress = !!(street1 || street2 || city || state || zip);

      const payload = {
        name: trimmedName,
        phone: form.locationPhone.trim() || null,

        primaryContact: { name: contactName, email: contactEmail, phone: null, title: null },
        contact: contactName,
        contactName: contactName,
        email: contactEmail,
        contactEmail: contactEmail,

        stage: form.stage || "Prospect",
        flaggedAs: form.flaggedAs || "None",
        visited: !!form.visited,
        lastVisitedAt: form.visited ? lastVisitedAt : null,
        hasSampleOrder: !!form.hasSampleOrder,

        active: form.active !== false,

        notes: form.notes.trim() || null,

        addressStreet: street1,
        addressStreet2: street2,
        addressCity: city,
        addressState: state,
        addressZip: zip,

        city: city,
        state: state,
        zip: zip,

        address: hasAnyAddress
          ? {
              line1: street1,
              line2: street2,
              city: city,
              state: state,
              zip: zip,
              country: existingCountry || "USA",
            }
          : account?.address || null,

        customFields: cleanedCustom,
        updatedAt: serverTimestamp(),
      };

      if (isAdmin) {
        let territoryId = null;
        let territoryName = null;
        let territoryOverride = false;

        if (form.territoryOverride && form.territoryId) {
          territoryId = String(form.territoryId);
          const t = territoriesById.get(territoryId);
          territoryName = getTerritoryLabel(t) || territoryId;
          territoryOverride = true;
        } else {
          territoryId = autoTerritory?.id ? String(autoTerritory.id) : null;
          territoryName = autoTerritory?.name || null;
          territoryOverride = false;
        }

        Object.assign(payload, {
          assignedToId,
          assignedUserId: assignedToId,
          assignedToName,
          territoryId,
          territoryName,
          territoryOverride,
          groupIds: uniqStrings(form.groupIds).filter(Boolean),
        });
      } else {
        Object.assign(payload, {
          groupIds: uniqStrings(existingGroupIds).filter(Boolean),
        });
      }

      await updateDoc(ref, payload);
      savedOk = true;
    } catch (err) {
      console.error("[AccountsEditPanel] Error saving:", err);
      alert(`Could not save changes: ${err?.message || "Unknown error"}`);
    } finally {
      if (mountedRef.current) setSaving(false);
    }

    if (!savedOk) return;

    onClose?.();
    if (onUpdated) {
      Promise.resolve(onUpdated()).catch((err) => {
        console.warn("[AccountsEditPanel] onUpdated failed:", err);
      });
    }
  };

  const handleDelete = async () => {
    if (!isAdmin) return;
    if (!window.confirm("Move this location to the recycle bin? You can restore it later from Admin.")) return;

    try {
      setSaving(true);
      const auth = getAuth();
      await moveDocToRecycleBin({
        db,
        companyId,
        entityType: "account",
        entityId: accountId,
        data: account || {},
        displayName: account?.name || account?.accountName || accountId,
        deletedByUid: auth.currentUser?.uid || null,
        deletedByRole: isAdmin ? "admin" : null,
      });
      await deleteDoc(doc(db, "companies", companyId, "accounts", accountId));

      onClose?.();

      if (onUpdated) {
        Promise.resolve(onUpdated()).catch((err) => {
          console.warn("[AccountsEditPanel] onUpdated failed after delete:", err);
        });
      }
    } catch (err) {
      console.error("[AccountsEditPanel] Delete error:", err);
      alert(`Could not move location to recycle bin: ${err?.message || "Unknown error"}`);
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const assignedUserLabel =
    assignedUser?.fullName ||
    assignedUser?.displayName ||
    assignedUser?.email ||
    account?.assignedToName ||
    account?.assignedUserName ||
    "";

  const territorySelectValue = form.territoryOverride ? (form.territoryId || "") : AUTO_TERRITORY;

  const hasPortalLinks =
    (portalLinks.activeUsers?.length || 0) > 0 || (portalLinks.pendingInvites?.length || 0) > 0;

  // ✅ Robust navigation: relative-first + fallback (fixes “goes to dashboard”)
  // otherwise uses /team. Includes a fallback to the other form.
  const openTeamInvites = (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (e?.stopPropagation) e.stopPropagation();

    const search = `?tab=invites&accountId=${encodeURIComponent(accountId)}`;
    navigate({ pathname: "/team", search });
  };


  const coverageCard = (
    <div className="bg-white border border-slate-200 rounded-xl p-3 min-w-0">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Users className="w-4 h-4 text-blue-700 shrink-0" />
          <h3 className="text-sm font-semibold text-slate-900 truncate">Coverage</h3>
        </div>
        {!isAdmin ? (
          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
            <Lock className="w-3 h-3" /> Admin Only
          </span>
        ) : null}
      </div>

      <div className="text-[11px] text-slate-500 mb-2">
        Auto-detect uses <span className="font-medium">Zip</span>, then{" "}
        <span className="font-medium">City</span>, then <span className="font-medium">State</span>.
      </div>

      <div className="space-y-2">
        <FieldRow label="Assigned">
          {isAdmin ? (
            <select
              className={inputBase}
              value={form.assignedToId}
              disabled={staffLoading}
              onChange={(e) => handleChange("assignedToId", e.target.value)}
            >
              {staffLoading ? <option value="">Loading staff…</option> : <option value="">Unassigned</option>}
              {teamUsers.map((u) => {
                const label = u.fullName || u.displayName || u.email || u.id;
                const role = u.role ? ` (${u.role})` : "";
                return (
                  <option key={u.id} value={u.id}>
                    {label}
                    {role}
                  </option>
                );
              })}
            </select>
          ) : (
            <input
              className={`${inputBase} ${inputDisabled}`}
              value={assignedUserLabel || "Unassigned"}
              readOnly
            />
          )}
        </FieldRow>

        <FieldRow label="Territory">
          {isAdmin ? (
            <div className="w-full min-w-0">
              <select
                className={inputBase}
                value={territorySelectValue}
                disabled={staticLoading}
                onChange={(e) => {
                  const val = e.target.value;

                  if (val === AUTO_TERRITORY) {
                    setForm((prev) => ({
                      ...prev,
                      territoryOverride: false,
                      territoryId: "",
                    }));
                    return;
                  }

                  setForm((prev) => ({
                    ...prev,
                    territoryOverride: true,
                    territoryId: val || "",
                  }));
                }}
              >
                <option value={AUTO_TERRITORY}>
                  {autoTerritoryLabel ? `Auto-detect: ${autoTerritoryLabel}` : "Auto-detect (no match)"}
                </option>

                {territories.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name || t.center?.name || t.label || t.id}
                  </option>
                ))}
              </select>

              <div className="mt-1 text-[10px] text-slate-500 flex items-center gap-2">
                <span className="truncate">
                  {effectiveTerritoryLabel}
                  {!form.territoryOverride ? ` · ${autoTerritoryDetail}` : ""}
                </span>
                <span
                  className={`px-1.5 py-0.5 rounded-full border text-[9px] ${
                    form.territoryOverride
                      ? "bg-amber-50 text-amber-800 border-amber-200"
                      : "bg-blue-50 text-blue-800 border-blue-200"
                  }`}
                >
                  {form.territoryOverride ? "Manual" : "Auto"}
                </span>
              </div>
            </div>
          ) : (
            <input className={`${inputBase} ${inputDisabled}`} value={effectiveTerritoryLabel} readOnly />
          )}
        </FieldRow>

        {isAdmin ? (
          <div className="pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs font-semibold text-slate-900">Visibility Groups</div>
              <div className="text-[11px] text-slate-500">
                Selected:{" "}
                <span className="font-semibold text-blue-600">
                  {Array.isArray(form.groupIds) ? form.groupIds.length : 0}
                </span>
              </div>
            </div>

            <input
              value={groupSearch}
              onChange={(e) => setGroupSearch(e.target.value)}
              placeholder="Search groups..."
              disabled={staticLoading}
              className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-xs mb-2 bg-white disabled:bg-slate-50"
            />

            <div className="border border-slate-200 rounded-lg bg-white max-h-56 overflow-y-auto">
              {staticLoading ? (
                <div className="p-3 text-xs text-slate-500 text-center inline-flex items-center gap-2 w-full justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading groups…
                </div>
              ) : filteredGroups.length === 0 ? (
                <div className="p-3 text-xs text-slate-500 text-center">No groups found</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {filteredGroups.map((g) => {
                    const checked = Array.isArray(form.groupIds) && form.groupIds.includes(g.id);
                    const isStaffGroup = g.id === companyData?.staffGroupId;
                    return (
                      <li key={g.id} className="px-3 py-2 hover:bg-slate-50">
                        <label className="flex items-center gap-2 text-xs min-w-0 cursor-pointer">
                          <input
                            type="checkbox"
                            className="w-4 h-4 shrink-0"
                            checked={checked}
                            onChange={() => toggleGroup(g.id)}
                          />
                          <span className="flex-1 min-w-0 truncate">{g.name || g.id}</span>
                          {isStaffGroup && (
                            <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 border-blue-200">
                              All Staff
                            </span>
                          )}
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="mt-2 text-[10px] text-slate-500">
              Admins can change account visibility groups.
            </div>
          </div>
        ) : (
          <div className="pt-2 border-t border-slate-100 text-[11px] text-slate-500">
            Groups are managed by admins.
          </div>
        )}
      </div>
    </div>
  );

  const portalCard = (
    <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900">Portal Access</div>
          <div className="text-[11px] text-slate-600 truncate">
            {hasPortalLinks ? (
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-700" />
                Connected
              </span>
            ) : (
              <span className="inline-flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-500" />
                Not connected
              </span>
            )}
          </div>
        </div>

        {portalLoading ? (
          <div className="text-[11px] text-slate-600 whitespace-nowrap inline-flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        ) : (
          <div className="text-[11px] text-slate-700 whitespace-nowrap">
            <span className="font-semibold text-green-700">{portalLinks.activeUsers.length}</span> active ·{" "}
            <span className="font-semibold text-amber-700">{portalLinks.pendingInvites.length}</span> pending
          </div>
        )}
      </div>

      {portalLinks.activeUsers.length > 0 ? (
        <div className="bg-white border border-blue-200 rounded-lg p-2">
          <div className="text-[11px] font-semibold text-slate-900 mb-1">Active portal users</div>
          <ul className="space-y-1">
            {portalLinks.activeUsers.slice(0, 5).map((u) => (
              <li key={u.id} className="text-[11px] text-slate-700 flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-green-700" />
                <span className="truncate flex-1">
                  {(u.fullName || u.displayName || u.email || "Customer") +
                    (u.email ? ` · ${u.email}` : "")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {portalLinks.pendingInvites.length > 0 ? (
        <div className="bg-white border border-blue-200 rounded-lg p-2">
          <div className="text-[11px] font-semibold text-slate-900 mb-1">Pending portal invites</div>
          <ul className="space-y-1">
            {portalLinks.pendingInvites.slice(0, 5).map((i) => (
              <li key={i.id} className="text-[11px] text-slate-700 flex items-center gap-2">
                <Clock className="w-3.5 h-3.5 text-amber-700" />
                <span className="truncate flex-1">
                  {(i.email || "—") + (i.createdAt ? ` · ${formatDate(i.createdAt)}` : "")}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <button
        type="button"
        onClick={openTeamInvites}
        disabled={!canManagePortalAccess}
        className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold ${
          !canManagePortalAccess
            ? "bg-slate-200 text-slate-500 cursor-not-allowed"
            : "bg-blue-600 text-white hover:bg-blue-700"
        }`}
        title={!canManagePortalAccess ? "Portal invites are unavailable for customer users." : "Open Team → Invites"}
      >
        <ExternalLink className="w-4 h-4" />
        Manage Portal Access
      </button>

      <div className="text-[10px] text-slate-600">
        Portal invites are managed in <span className="font-semibold">Team → Invites</span>.
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center p-0 sm:items-center sm:p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />

      <div
        className="relative flex w-full max-w-6xl max-h-[94vh] flex-col overflow-hidden rounded-t-3xl border border-slate-200 bg-white shadow-2xl sm:max-h-[90vh] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5 sm:px-6 sm:py-4">
          <div className="min-w-0">
            <h2 className="truncate text-lg font-semibold text-slate-900">
              Edit Location{account?.name ? ` — ${account.name}` : ""}
            </h2>
            <p className="mt-0.5 truncate text-xs text-slate-500">
              {isAdmin ? "Admins can edit coverage, territory, and groups." : "Coverage is managed by admins."}
            </p>

            {(staticLoading || staffLoading) && (
              <div className="mt-1 text-[11px] text-slate-600 inline-flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                {staticLoading ? "Loading company settings…" : "Loading staff…"}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors"
            aria-label="Close"
            type="button"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-3 sm:p-6">
          <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:grid-cols-3 lg:gap-6">
            <div className="min-w-0 space-y-3 sm:space-y-4 lg:col-span-2 lg:space-y-5">
              <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
                <h3 className="text-sm font-semibold text-slate-900 mb-2">Location</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <FieldRow label="Name" required>
                    <input
                      type="text"
                      className={inputBase}
                      value={form.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      required
                    />
                  </FieldRow>

                  <FieldRow label="Phone">
                    <input
                      type="tel"
                      className={inputBase}
                      value={form.locationPhone}
                      onChange={(e) => handleChange("locationPhone", e.target.value)}
                      placeholder="(###) ###-####"
                    />
                  </FieldRow>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
                <h3 className="text-sm font-semibold text-slate-900 mb-2">Primary Contact</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <FieldRow label="Contact">
                    <input
                      type="text"
                      className={inputBase}
                      value={form.primaryContactName}
                      onChange={(e) => handleChange("primaryContactName", e.target.value)}
                    />
                  </FieldRow>

                  <FieldRow label="Email">
                    <input
                      type="email"
                      className={inputBase}
                      value={form.primaryContactEmail}
                      onChange={(e) => handleChange("primaryContactEmail", e.target.value)}
                    />
                  </FieldRow>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4">
                <h3 className="text-sm font-semibold text-slate-900 mb-2">Address</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <FieldRow label="Street">
                    <input
                      type="text"
                      className={inputBase}
                      value={form.addressStreet}
                      onChange={(e) => handleChange("addressStreet", e.target.value)}
                    />
                  </FieldRow>

                  <FieldRow label="Street 2">
                    <input
                      type="text"
                      className={inputBase}
                      value={form.addressStreet2}
                      onChange={(e) => handleChange("addressStreet2", e.target.value)}
                    />
                  </FieldRow>

                  <div className="md:col-span-2">
                    <FieldRow label="City / State / Zip">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 min-w-0">
                        <input
                          type="text"
                          className={inputBase}
                          value={form.addressCity}
                          onChange={(e) => handleChange("addressCity", e.target.value)}
                          placeholder="City"
                        />
                        <select
                          className={inputBase}
                          value={form.addressState}
                          onChange={(e) => handleChange("addressState", e.target.value)}
                        >
                          <option value="">State</option>
                          {US_STATES.map((s) => (
                            <option key={s.value} value={s.value}>
                              {s.value}
                            </option>
                          ))}
                        </select>
                        <input
                          type="text"
                          className={inputBase}
                          value={form.addressZip}
                          onChange={(e) => handleChange("addressZip", e.target.value)}
                          placeholder="Zip"
                        />
                      </div>
                    </FieldRow>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
                <h3 className="text-sm font-semibold text-slate-900 mb-2">Relationship & Behavior</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <FieldRow label="Stage">
                    <select
                      className={inputBase}
                      value={form.stage}
                      onChange={(e) => handleChange("stage", e.target.value)}
                    >
                      {STAGES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </FieldRow>

                  <FieldRow label="Flag">
                    <select
                      className={inputBase}
                      value={form.flaggedAs}
                      onChange={(e) => handleChange("flaggedAs", e.target.value)}
                    >
                      {FLAGS.map((f) => (
                        <option key={f} value={f}>
                          {f}
                        </option>
                      ))}
                    </select>
                  </FieldRow>

                  <FieldRow
                    label="Visited"
                    right={
                      <label className="inline-flex items-center gap-2 text-xs text-slate-700">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={!!form.visited}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setForm((prev) => ({
                              ...prev,
                              visited: checked,
                              lastVisitedDate:
                                checked && !prev.lastVisitedDate
                                  ? new Date().toISOString().split("T")[0]
                                  : prev.lastVisitedDate,
                            }));
                          }}
                        />
                      </label>
                    }
                  >
                    {form.visited ? (
                      <input
                        type="date"
                        className={inputBase}
                        value={form.lastVisitedDate}
                        onChange={(e) => handleChange("lastVisitedDate", e.target.value)}
                      />
                    ) : (
                      <input className={`${inputBase} ${inputDisabled}`} value="Not marked visited" readOnly />
                    )}
                  </FieldRow>

                  <FieldRow label="Sample / Active">
                    <div className="flex flex-wrap items-center gap-4 text-xs text-slate-700">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={!!form.hasSampleOrder}
                          onChange={(e) => handleChange("hasSampleOrder", e.target.checked)}
                        />
                        Sample
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={!!form.active}
                          onChange={(e) => handleChange("active", e.target.checked)}
                        />
                        Active
                      </label>
                    </div>
                  </FieldRow>

                  <div className="md:col-span-2">
                    <FieldRow label="Notes">
                      <textarea
                        rows={2}
                        className="w-full border border-slate-300 rounded-lg px-3 py-1 text-xs resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={form.notes}
                        onChange={(e) => handleChange("notes", e.target.value)}
                        placeholder="Account history, context..."
                      />
                    </FieldRow>
                  </div>
                </div>
              </div>

              {Array.isArray(fieldDefs) && fieldDefs.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-3">
                  <h3 className="text-sm font-semibold text-slate-900 mb-2">Custom Fields</h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {fieldDefs
                      .filter((def) => def.showInForm !== false)
                      .filter((def) => !isReservedCustomFieldDef(def))
                      .map((def) => {
                        const key = def.fieldKey || def.id;
                        const type = (def.type || "text").toLowerCase();
                        const value = customValues[key];

                        if (type === "boolean") {
                          return (
                            <FieldRow key={key} label={def.label || key}>
                              <div className="flex justify-end">
                                <input
                                  type="checkbox"
                                  className="h-4 w-4"
                                  checked={!!value}
                                  onChange={(e) => handleCustomChange(key, e.target.checked)}
                                />
                              </div>
                            </FieldRow>
                          );
                        }

                        if (type === "select") {
                          const options = Array.isArray(def.options)
                            ? def.options
                            : String(def.options || "")
                                .split(",")
                                .map((s) => s.trim())
                                .filter(Boolean);

                          return (
                            <FieldRow key={key} label={def.label || key}>
                              <select
                                className={inputBase}
                                value={value ?? ""}
                                onChange={(e) => handleCustomChange(key, e.target.value)}
                              >
                                <option value="">Select…</option>
                                {options.map((opt) => (
                                  <option key={opt} value={opt}>
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            </FieldRow>
                          );
                        }

                        if (type === "number") {
                          return (
                            <FieldRow key={key} label={def.label || key}>
                              <input
                                type="number"
                                className={inputBase}
                                value={value ?? ""}
                                onChange={(e) => handleCustomChange(key, e.target.value)}
                              />
                            </FieldRow>
                          );
                        }

                        return (
                          <FieldRow key={key} label={def.label || key}>
                            <input
                              type="text"
                              className={inputBase}
                              value={value ?? ""}
                              onChange={(e) => handleCustomChange(key, e.target.value)}
                            />
                          </FieldRow>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>

            <div className="min-w-0 space-y-3 sm:space-y-4">
              {coverageCard}
              {portalCard}
            </div>
          </div>

          {isAdmin ? (
            <div className="mt-4 border-t border-slate-200 pt-4 sm:mt-6 sm:pt-5">
              <button
                type="button"
                onClick={handleDelete}
                disabled={saving}
                className="flex items-center justify-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60"
                title="Delete Location"
              >
                <Trash2 className="w-4 h-4" />
                Delete Location
              </button>
            </div>
          ) : null}
        </form>

        <div className="sticky bottom-0 flex flex-row items-center justify-end gap-3 border-t border-slate-200 bg-slate-50/95 px-4 py-3 backdrop-blur sm:px-6 sm:py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50"
            disabled={saving}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountsEditPanel;
